import { useState, useEffect, useCallback } from "react";
import { 
  CheckCircle2, Circle, AlertTriangle, Clock, Ban, ZapOff, 
  ChevronDown, ChevronUp, Plus, X, RefreshCw, Flag, User,
  CalendarDays, FileText, AlertCircle, Zap, MessageSquare, Send, Trash2, Sparkles, BellRing
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Constants ─────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  not_started: { color: "#3A3A3C", bg: "rgba(58,58,60,0.15)", label: "Not Started", Icon: Circle },
  upcoming:    { color: "#0A84FF", bg: "rgba(10,132,255,0.1)", label: "Upcoming",    Icon: Clock },
  active:      { color: "#D4AF37", bg: "rgba(212,175,55,0.1)", label: "Active",      Icon: Zap },
  completed:   { color: "#00FF88", bg: "rgba(0,255,136,0.1)", label: "Completed",   Icon: CheckCircle2 },
  overdue:     { color: "#FF3B30", bg: "rgba(255,59,48,0.1)", label: "Overdue",     Icon: AlertTriangle },
  blocked:     { color: "#FF9F0A", bg: "rgba(255,159,10,0.1)", label: "Blocked",    Icon: Ban },
  canceled:    { color: "#8E8E93", bg: "rgba(142,142,147,0.1)", label: "Canceled",  Icon: ZapOff },
};

const HEALTH_CONFIG = {
  "On Track":       { color: "#00FF88", bg: "rgba(0,255,136,0.08)", border: "rgba(0,255,136,0.2)" },
  "Needs Attention":{ color: "#FF9F0A", bg: "rgba(255,159,10,0.08)", border: "rgba(255,159,10,0.2)" },
  "At Risk":        { color: "#FF3B30", bg: "rgba(255,59,48,0.08)", border: "rgba(255,59,48,0.2)" },
  "Blocked":        { color: "#FF3B30", bg: "rgba(255,59,48,0.08)", border: "rgba(255,59,48,0.2)" },
};

const ROLE_LABELS = {
  investor: "Investor",
  lender: "Lender",
  contractor: "Contractor",
  realtor: "Realtor",
  title: "Title",
};

const MILESTONE_TYPES = [
  "deal_created","underwriting_started","offer_sent","offer_accepted",
  "contract_signed","earnest_money_due","inspection_period","title_opened",
  "lender_review","contractor_walkthrough","draw_review","clear_to_close",
  "closing","disbursement","follow_up","custom",
];

// ── Sub-components ────────────────────────────────────────────────────

function HealthPanel({ health, dealId }) {
  if (!health) return null;
  const cfg = HEALTH_CONFIG[health.health_label] || HEALTH_CONFIG["On Track"];

  return (
    <div className="rounded-2xl border p-5 mb-6" style={{ background: cfg.bg, borderColor: cfg.border }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: cfg.color, boxShadow: `0 0 8px ${cfg.color}` }} />
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: cfg.color }}>
              Deal Health
            </span>
            <h3 className="text-lg font-bold text-white mt-0.5">{health.health_label}</h3>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <Metric label="Complete" value={`${health.pct_complete}%`} color="#00FF88" />
          {health.days_to_close != null && (
            <Metric
              label="Days to Close"
              value={health.days_to_close < 0 ? "Past Due" : `${health.days_to_close}d`}
              color={health.days_to_close < 0 ? "#FF3B30" : "#D4AF37"}
            />
          )}
          {health.overdue_count > 0 && (
            <Metric label="Overdue" value={health.overdue_count} color="#FF3B30" />
          )}
          {health.blocked_count > 0 && (
            <Metric label="Blocked" value={health.blocked_count} color="#FF9F0A" />
          )}
          <Metric label="Upcoming" value={health.upcoming_count} color="#0A84FF" />
        </div>
      </div>

      {/* Next Critical Action */}
      {health.next_action && (
        <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-start gap-2">
          <Flag className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-[10px] text-white/30 uppercase tracking-wider">Next Critical Action</span>
            <p className="text-sm text-white/80 mt-0.5">
              <span className="font-semibold text-[#D4AF37]">{health.next_action.title}</span>
              {health.next_action.due_date && (
                <span className="text-white/40 ml-2">— due {health.next_action.due_date}</span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
      <div className="text-[10px] text-white/30 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function MilestoneCard({ milestone, onUpdate, onDelete, onRemind }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [form, setForm] = useState({
    status: milestone.status,
    due_date: milestone.due_date || "",
    notes: milestone.notes || "",
    blocker_reason: milestone.blocker_reason || "",
  });

  const cfg = STATUS_CONFIG[milestone.status] || STATUS_CONFIG.not_started;
  const { Icon } = cfg;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/timeline/${milestone.deal_id}/milestones/${milestone.milestone_id}`,
        {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) throw new Error();
      toast.success("Milestone updated");
      onUpdate({ ...milestone, ...form });
      setEditing(false);
    } catch {
      toast.error("Failed to update milestone");
    } finally {
      setSaving(false);
    }
  };

  const quickComplete = async () => {
    setSaving(true);
    try {
      await fetch(
        `${API}/api/timeline/${milestone.deal_id}/milestones/${milestone.milestone_id}`,
        {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "completed" }),
        }
      );
      toast.success("Marked complete");
      onUpdate({ ...milestone, status: "completed" });
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await fetch(
        `${API}/api/timeline/${milestone.deal_id}/milestones/${milestone.milestone_id}`,
        { method: "DELETE", credentials: "include" }
      );
      toast.success("Deleted");
      onDelete(milestone.milestone_id);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const isComplete = milestone.status === "completed";
  const isCanceled = milestone.status === "canceled";

  return (
    <div
      className="border rounded-xl overflow-hidden transition-all duration-200"
      style={{ borderColor: `${cfg.color}30`, background: expanded ? cfg.bg : "transparent" }}
      data-testid={`milestone-${milestone.milestone_id}`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 p-4">
        {/* Status dot / complete button */}
        <button
          onClick={isComplete || isCanceled ? undefined : quickComplete}
          disabled={saving || isComplete || isCanceled}
          title={isComplete ? "Completed" : "Mark as complete"}
          className="flex-shrink-0 transition-transform hover:scale-110 disabled:cursor-default"
        >
          <Icon className="w-5 h-5" style={{ color: cfg.color }} strokeWidth={isComplete ? 2.5 : 1.5} />
        </button>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-semibold ${isComplete || isCanceled ? "text-white/30 line-through" : "text-white"}`}>
              {milestone.title}
            </span>
            {milestone.is_critical && (
              <span className="text-[9px] px-1.5 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 rounded font-bold uppercase tracking-wider">
                Critical
              </span>
            )}
            {milestone.auto_generated && (
              <span className="text-[9px] px-1.5 py-0.5 bg-white/[0.04] text-white/20 rounded">
                Auto
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {milestone.due_date && (
              <span className="text-[11px] text-white/30 flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {milestone.due_date}
              </span>
            )}
            <span
              className="text-[11px] font-medium px-1.5 py-0.5 rounded"
              style={{ color: cfg.color, background: cfg.bg }}
            >
              {cfg.label}
            </span>
            <span className="text-[11px] text-white/20">
              {ROLE_LABELS[milestone.assigned_role] || milestone.assigned_role}
            </span>
          </div>
        </div>

        {/* Expand / actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isComplete && !isCanceled && (
            <button
              onClick={() => { setEditing(!editing); setExpanded(true); }}
              className="p-1.5 rounded-lg text-white/20 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
              title="Edit"
            >
              <FileText className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => onRemind && onRemind(milestone)}            className="p-1.5 rounded-lg text-white/20 hover:text-blue-400 hover:bg-blue-400/10 transition-all"
            title="Send SMS reminder"
            data-testid={`remind-btn-${milestone.milestone_id}`}
          >
            <BellRing className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-lg text-white/20 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-all"
            title="Delete"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg text-white/20 hover:text-white transition-all"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/[0.04] pt-3 space-y-3">
          {milestone.description && (
            <p className="text-sm text-white/40">{milestone.description}</p>
          )}
          {milestone.blocker_reason && (
            <div className="flex items-start gap-2 p-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#FF9F0A]">{milestone.blocker_reason}</p>
            </div>
          )}
          {milestone.notes && !editing && (
            <p className="text-xs text-white/30 italic">{milestone.notes}</p>
          )}

          {!editing && <CommentsSection milestone={milestone} />}

          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#D4AF37]/40"
                  >
                    {["not_started","upcoming","active","completed","overdue","blocked","canceled"].map(s => (
                      <option key={s} value={s} style={{ background: "#111" }}>
                        {STATUS_CONFIG[s]?.label || s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Due Date</label>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-[#D4AF37]/40"
                  />
                </div>
              </div>
              {form.status === "blocked" && (
                <input
                  value={form.blocker_reason}
                  onChange={e => setForm(f => ({ ...f, blocker_reason: e.target.value }))}
                  placeholder="Blocker reason..."
                  className="w-full bg-black/40 border border-[#FF9F0A]/30 rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/20 focus:outline-none"
                />
              )}
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Notes..."
                className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-[#D4AF37]/40"
              />
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="flex-1 py-1.5 text-xs text-white/40 border border-white/[0.08] rounded-lg hover:text-white transition-all">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving} data-testid={`save-milestone-${milestone.milestone_id}`}
                  className="flex-1 py-1.5 text-xs font-bold text-black bg-[#D4AF37] rounded-lg disabled:opacity-50 flex items-center justify-center gap-1">
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommentsSection({ milestone }) {
  const [comments, setComments]     = useState([]);
  const [text, setText]             = useState("");
  const [loading, setLoading]       = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/timeline/${milestone.deal_id}/milestones/${milestone.milestone_id}/comments`,
          { credentials: "include" }
        );
        if (res.ok) {
          const d = await res.json();
          setComments(d.comments || []);
        }
      } catch {/* silent */} finally { setLoading(false); }
    };
    fetchComments();
  }, [milestone.deal_id, milestone.milestone_id]);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API}/api/timeline/${milestone.deal_id}/milestones/${milestone.milestone_id}/comments`,
        {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: text.trim() }),
        }
      );
      if (!res.ok) throw new Error();
      const d = await res.json();
      setComments(prev => [...prev, d.comment]);
      setText("");
    } catch {
      toast.error("Failed to post comment");
    } finally { setSubmitting(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      await fetch(
        `${API}/api/timeline/${milestone.deal_id}/milestones/${milestone.milestone_id}/comments/${commentId}`,
        { method: "DELETE", credentials: "include" }
      );
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch {
      toast.error("Failed to delete comment");
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " +
           d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/[0.04]">
      <div className="flex items-center gap-1.5 mb-2">
        <MessageSquare className="w-3 h-3 text-white/20" />
        <span className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">
          Activity {comments.length > 0 ? `(${comments.length})` : ""}
        </span>
      </div>

      {/* Comment thread */}
      {loading ? (
        <div className="text-[11px] text-white/20 py-2">Loading...</div>
      ) : comments.length > 0 ? (
        <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
          {comments.map(c => (
            <div key={c.comment_id} className="flex items-start gap-2 group">
              <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[8px] font-bold text-[#D4AF37]">
                  {(c.author_name || "U").charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-white/70">{c.author_name}</span>
                  <span className="text-[9px] text-white/20">{formatTime(c.created_at)}</span>
                </div>
                <p className="text-[11px] text-white/50 mt-0.5 leading-relaxed">{c.text}</p>
              </div>
              <button
                onClick={() => deleteComment(c.comment_id)}
                className="opacity-0 group-hover:opacity-100 p-0.5 text-white/20 hover:text-red-400 transition-all flex-shrink-0"
                title="Delete"
                data-testid={`delete-comment-${c.comment_id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-white/20 mb-2 italic">No comments yet. Add an update.</p>
      )}

      {/* Input */}
      <div className="flex gap-2">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && submit()}
          placeholder="Add a comment or update..."
          className="flex-1 bg-black/40 border border-white/[0.06] rounded-lg px-3 py-1.5 text-[11px] text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/30"
          data-testid={`comment-input-${milestone.milestone_id}`}
        />
        <button
          onClick={submit}
          disabled={submitting || !text.trim()}
          className="px-2.5 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded-lg disabled:opacity-30 hover:bg-[#D4AF37]/20 transition-all"
          data-testid={`comment-submit-${milestone.milestone_id}`}
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


function AddMilestoneModal({ dealId, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", milestone_type: "custom", due_date: "", assigned_role: "investor",
    is_critical: false, description: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/timeline/${dealId}/milestones`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success("Milestone added");
      onCreated(data.milestone);
      onClose();
    } catch {
      toast.error("Failed to create milestone");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold">Add Milestone</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <input
          autoFocus value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Milestone title"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
          data-testid="milestone-title-input"
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Type</label>
            <select value={form.milestone_type} onChange={e => setForm(f => ({ ...f, milestone_type: e.target.value }))}
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white focus:outline-none">
              {MILESTONE_TYPES.map(t => (
                <option key={t} value={t} style={{ background: "#111" }}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Assigned Role</label>
            <select value={form.assigned_role} onChange={e => setForm(f => ({ ...f, assigned_role: e.target.value }))}
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white focus:outline-none">
              {["investor","lender","contractor","realtor","title"].map(r => (
                <option key={r} value={r} style={{ background: "#111" }}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Due Date (optional)</label>
          <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
            className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
        </div>

        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
          placeholder="Description (optional)"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-[#D4AF37]/40" />

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_critical} onChange={e => setForm(f => ({ ...f, is_critical: e.target.checked }))}
            className="w-4 h-4 accent-[#D4AF37]" />
          <span className="text-sm text-white/60">Mark as critical</span>
        </label>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-white/[0.08] text-white/50 rounded-xl text-sm hover:text-white transition-all">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-new-milestone-btn"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-black font-bold rounded-xl text-sm disabled:opacity-50">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Milestone
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Role filter tabs ───────────────────────────────────────────────────

const ROLES = [
  { value: "all", label: "All" },
  { value: "investor", label: "Investor" },
  { value: "lender", label: "Lender" },
  { value: "contractor", label: "Contractor" },
  { value: "realtor", label: "Realtor" },
  { value: "title", label: "Title" },
];

// ── Main component ─────────────────────────────────────────────────────

export default function DealTimelineTab({ dealId }) {
  const [milestones, setMilestones] = useState([]);
  const [health, setHealth]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeRole, setActiveRole] = useState("all");
  const [showAdd, setShowAdd]       = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [msRes, healthRes] = await Promise.all([
        fetch(`${API}/api/timeline/${dealId}/milestones`, { credentials: "include" }),
        fetch(`${API}/api/timeline/${dealId}/health`, { credentials: "include" }),
      ]);
      const msData     = await msRes.json();
      const healthData = await healthRes.json();
      setMilestones(msData.milestones || []);
      setHealth(healthData);
    } catch {
      toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdate = (updated) => {
    setMilestones(prev => prev.map(m => m.milestone_id === updated.milestone_id ? { ...m, ...updated } : m));
    // Refresh health panel
    fetch(`${API}/api/timeline/${dealId}/health`, { credentials: "include" })
      .then(r => r.json()).then(setHealth).catch(() => {});
  };

  const handleDelete = (id) => setMilestones(prev => prev.filter(m => m.milestone_id !== id));
  const handleCreated = (m) => { setMilestones(prev => [...prev, m]); fetchData(); };

  const triggerAuto = async () => {
    setTriggering(true);
    try {
      await fetch(`${API}/api/timeline/${dealId}/auto-generate`, { method: "POST", credentials: "include" });
      toast.success("Auto-generated milestones from current stage");
      fetchData();
    } catch {
      toast.error("Failed");
    } finally {
      setTriggering(false);
    }
  };

  const handleSendReminder = async (milestone) => {
    try {
      const res = await fetch(
        `${API}/api/timeline/${dealId}/milestones/${milestone.milestone_id}/remind`,
        { method: "POST", credentials: "include" }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Failed to send reminder");
        return;
      }
      if (data.demo_mode) {
        toast.info("SMS simulated (demo mode)");
      } else {
        toast.success(`Reminder sent to ${data.to}`);
      }
    } catch {
      toast.error("Failed to send SMS reminder");
    }
  };

  const triggerAI = async () => {    setAiGenerating(true);
    try {
      const res = await fetch(`${API}/api/timeline/${dealId}/ai-timeline`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`AI created ${data.milestones_created} milestones for this deal`);
      if (data.summary) toast.info(data.summary, { duration: 6000 });
      fetchData();
    } catch {
      toast.error("AI generation failed — please retry");
    } finally {
      setAiGenerating(false);
    }
  };

  const filtered = activeRole === "all"
    ? milestones
    : milestones.filter(m => m.assigned_role === activeRole);

  // Group by status priority
  const sorted = [...filtered].sort((a, b) => {
    const order = ["overdue","blocked","active","upcoming","not_started","completed","canceled"];
    return order.indexOf(a.status) - order.indexOf(b.status);
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="deal-timeline-tab">
      {/* Health Panel */}
      <HealthPanel health={health} dealId={dealId} />

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Role filter */}
        <div className="flex items-center gap-1 bg-black/30 border border-white/[0.06] rounded-xl p-1">
          {ROLES.map(r => (
            <button
              key={r.value}
              onClick={() => setActiveRole(r.value)}
              data-testid={`role-filter-${r.value}`}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-all ${
                activeRole === r.value
                  ? "bg-[#D4AF37] text-black"
                  : "text-white/40 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={triggerAuto}
            disabled={triggering}
            data-testid="auto-generate-btn"
            className="flex items-center gap-1.5 px-3 py-2 border border-white/[0.08] text-white/40 hover:text-[#D4AF37] hover:border-[#D4AF37]/30 rounded-xl text-xs transition-all disabled:opacity-50"
          >
            {triggering ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Auto-Generate
          </button>
          <button
            onClick={triggerAI}
            disabled={aiGenerating}
            data-testid="ai-generate-btn"
            title="Generate a smart, deal-specific timeline using AI"
            className="flex items-center gap-1.5 px-3 py-2 border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50 rounded-xl text-xs transition-all disabled:opacity-50"
          >
            {aiGenerating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            AI Timeline
          </button>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-milestone-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold rounded-xl text-xs transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Add Milestone
          </button>
        </div>
      </div>

      {/* Milestone list */}
      {sorted.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-white/[0.06] rounded-2xl">
          <CalendarDays className="w-8 h-8 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">No milestones yet.</p>
          <p className="text-white/20 text-xs mt-1">Click "Auto-Generate" to create them from your deal stage, or add manually.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map(m => (
            <MilestoneCard
              key={m.milestone_id}
              milestone={m}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onRemind={handleSendReminder}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddMilestoneModal dealId={dealId} onClose={() => setShowAdd(false)} onCreated={handleCreated} />
      )}
    </div>
  );
}
